import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getCaptains, createCaptain, getVenues, createVenue } from '../services/api';
import { getAllSportConfigs } from '../utils/sportConfig';
import SearchableSelect from './SearchableSelect';
import './ScheduleForm.css';

const SKILL_LABELS = [
  { min: 1.0, max: 2.5,  label: 'Beginner' },
  { min: 2.5, max: 3.0,  label: 'Novice' },
  { min: 3.0, max: 3.5,  label: 'Intermediate' },
  { min: 3.5, max: 4.0,  label: 'Adv. Intermediate' },
  { min: 4.0, max: 4.5,  label: 'Advanced' },
  { min: 4.5, max: 5.0,  label: 'Expert' },
  { min: 5.0, max: 6.01, label: 'Professional' },
];
function getSkillLabel(rating) {
  const r = parseFloat(rating);
  const found = SKILL_LABELS.find(s => r >= s.min && r < s.max);
  return found ? found.label : '';
}

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'GU', name: 'Guam' }, { code: 'VI', name: 'U.S. Virgin Islands' },
  { code: 'AS', name: 'American Samoa' }, { code: 'MP', name: 'Northern Mariana Islands' },
];

function ScheduleForm({ onBack, leagueId: initialLeagueId }) {
  const { user } = useAuth();
  const userPlan = user?.plan || 'free';
  const { leagueId } = useParams();
  const actualLeagueId = leagueId || initialLeagueId;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Captain state
  const [captains, setCaptains] = useState([]);
  const [inlineCreateIndex, setInlineCreateIndex] = useState(null); // which team index is creating a captain
  const [inlineCreateGroup, setInlineCreateGroup] = useState(null); // 1 or 2
  const [inlineForm, setInlineForm] = useState({ name: '', email: '', phone: '' });
  const [inlineLoading, setInlineLoading] = useState(false);

  // Venue state
  const [venues, setVenues] = useState([]);
  const [venueTimeSlotMap, setVenueTimeSlotMap] = useState({}); // { timeSlot: venueId }
  const [inlineVenueSlot, setInlineVenueSlot] = useState(null); // which time slot is creating a venue
  const [inlineVenueForm, setInlineVenueForm] = useState({ name: '', address: '', fieldCount: 1 });
  const [inlineVenueLoading, setInlineVenueLoading] = useState(false);

  // Fetch user's captains on mount
  useEffect(() => {
    getCaptains()
      .then(result => setCaptains(result.captains || []))
      .catch(() => {}); // silent fail — captains are optional
  }, []);

  // Fetch user's venues on mount (Starter+ only)
  useEffect(() => {
    if (userPlan !== 'free') {
      getVenues()
        .then(result => setVenues(result.venues || []))
        .catch(() => {}); // silent fail — venues are optional
    }
  }, [userPlan]);

  // Date mode: 'manual' or 'auto'
  const [dateMode, setDateMode] = useState('auto');
  const [autoStartDate, setAutoStartDate] = useState('');
  const [autoNumWeeks, setAutoNumWeeks] = useState(10);

  const [formData, setFormData] = useState({
    leagueName: '',
    sport: 'general',
    formatType: 'standard',       // 'standard' | 'rotating_partners'
    city: '',
    state: '',
    numTeams: 8,
    gamesPerWeek: 4,
    weeks: [],
    teams: [],
    timeSlots: [],
    hasGroup2: false,
    numTeamsGroup2: 2,
    teamsGroup2: [],
    bestOf: '',
    enablePlayoffs: false,
    playoffTeams: 0
  });

  // Rotating partner player list
  const [rpPlayers, setRpPlayers] = useState([]);
  const [rpNewName, setRpNewName] = useState('');
  const [rpNewEmail, setRpNewEmail] = useState('');
  const [rpNewRating, setRpNewRating] = useState('3.5');
  const [rpEditIndex, setRpEditIndex] = useState(null);
  const [rpEditData, setRpEditData] = useState({});

  // Load league data if viewing existing league
  useEffect(() => {
    if (actualLeagueId) {
      loadLeagueData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualLeagueId]);

  const loadLeagueData = async () => {
    try {
      const response = await api.get(`/leagues/${actualLeagueId}`);
      const league = response.data.league;
      const originalInput = league.scheduleData?.originalInput;

      // Reconstruct weeks with holiday flags, falling back to gameDates only
      const weeks = originalInput?.allWeeks
        || (originalInput?.gameDates || []).map(date => ({
          date,
          isHoliday: false
        }));

      // Extract teams by group (with captain info)
      const group1Teams = (league.teams || []).filter(t => t.groupNumber !== 2).map(t => ({
        name: t.name,
        captainId: t.captainId || t.captain?.id || null
      }));
      const group2Teams = (league.teams || []).filter(t => t.groupNumber === 2).map(t => ({
        name: t.name,
        captainId: t.captainId || t.captain?.id || null
      }));

      // Restore time slots from originalInput
      const timeSlots = originalInput?.timeSlots || [];

      setFormData({
        leagueName: league.name,
        sport: league.sport || 'general',
        city: league.city || '',
        state: league.state || '',
        numTeams: group1Teams.length || 8,
        gamesPerWeek: timeSlots.length || 4,
        weeks,
        teams: group1Teams,
        timeSlots,
        hasGroup2: group2Teams.length > 0,
        numTeamsGroup2: group2Teams.length || 2,
        teamsGroup2: group2Teams,
        bestOf: league.bestOf || '',
        enablePlayoffs: league.playoffSettings?.enabled || false,
        playoffTeams: league.playoffSettings?.teamsQualifying || 0
      });
    } catch (err) {
      setError('Failed to load league data');
    }
  };

  // Auto-generate dates based on start date and number of weeks
  const generateAutoDates = () => {
    if (!autoStartDate || !autoNumWeeks) {
      setError('Please enter a start date and number of weeks');
      return;
    }

    const weeks = [];
    const startDate = new Date(autoStartDate);

    for (let i = 0; i < autoNumWeeks; i++) {
      const weekDate = new Date(startDate);
      weekDate.setDate(startDate.getDate() + (i * 7));
      
      weeks.push({
        date: weekDate.toISOString().split('T')[0],
        isHoliday: false
      });
    }

    setFormData(prev => ({ ...prev, weeks }));
    setError('');
  };

  // Toggle holiday status for a week
  const toggleHoliday = (index) => {
    const week = formData.weeks[index];
    const wasHoliday = week.isHoliday;
    const willBeHoliday = !wasHoliday;

    // Update the toggled week
    const updatedWeeks = formData.weeks.map((w, i) => 
      i === index ? { ...w, isHoliday: willBeHoliday } : w
    );

    // If marking as holiday (not unmarking), add a replacement week at the end
    if (willBeHoliday) {
      // Calculate next week date (7 days after the last week)
      const lastWeek = updatedWeeks[updatedWeeks.length - 1];
      const lastDate = new Date(lastWeek.date);
      const nextDate = new Date(lastDate);
      nextDate.setDate(lastDate.getDate() + 7);

      updatedWeeks.push({
        date: nextDate.toISOString().split('T')[0],
        isHoliday: false
      });
    }

    setFormData(prev => ({ ...prev, weeks: updatedWeeks }));
  };

  // Manual date handling
  const handleWeekDateChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      weeks: prev.weeks.map((week, i) => 
        i === index ? { ...week, date: value } : week
      )
    }));
  };

  const addManualWeek = () => {
    setFormData(prev => ({
      ...prev,
      weeks: [...prev.weeks, { date: '', isHoliday: false }]
    }));
  };

  const removeWeek = (index) => {
    setFormData(prev => ({
      ...prev,
      weeks: prev.weeks.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: ['numTeams', 'gamesPerWeek', 'numTeamsGroup2', 'playoffTeams'].includes(name) ? parseInt(value) : value
      };
      // Clear bestOf when switching away from pool/darts
      if (name === 'sport' && value !== 'pool' && value !== 'darts') {
        updated.bestOf = '';
      }
      // Reset formatType when switching away from pickleball
      if (name === 'sport' && value !== 'pickleball') {
        updated.formatType = 'standard';
      }
      return updated;
    });
  };

  const handleTeamChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      teams: prev.teams.map((team, i) =>
        i === index ? (typeof team === 'object' ? { ...team, name: value } : value) : team
      )
    }));
  };

  const handleTeamCaptainChange = (index, captainId) => {
    setFormData(prev => ({
      ...prev,
      teams: prev.teams.map((team, i) =>
        i === index ? { ...(typeof team === 'object' ? team : { name: team }), captainId: captainId || null } : team
      )
    }));
  };

  const handleTeamGroup2Change = (index, value) => {
    setFormData(prev => ({
      ...prev,
      teamsGroup2: prev.teamsGroup2.map((team, i) =>
        i === index ? (typeof team === 'object' ? { ...team, name: value } : value) : team
      )
    }));
  };

  const handleTeamGroup2CaptainChange = (index, captainId) => {
    setFormData(prev => ({
      ...prev,
      teamsGroup2: prev.teamsGroup2.map((team, i) =>
        i === index ? { ...(typeof team === 'object' ? team : { name: team }), captainId: captainId || null } : team
      )
    }));
  };

  const handleInlineCreate = async (groupNum, teamIndex) => {
    if (!inlineForm.name.trim()) return;
    setInlineLoading(true);
    try {
      const result = await createCaptain({
        name: inlineForm.name.trim(),
        email: inlineForm.email.trim() || null,
        phone: inlineForm.phone.trim() || null
      });
      const newCaptain = result.captain;
      setCaptains(prev => [...prev, newCaptain].sort((a, b) => a.name.localeCompare(b.name)));
      // Assign to the team
      if (groupNum === 1) {
        handleTeamCaptainChange(teamIndex, newCaptain.id);
      } else {
        handleTeamGroup2CaptainChange(teamIndex, newCaptain.id);
      }
      setInlineCreateIndex(null);
      setInlineCreateGroup(null);
      setInlineForm({ name: '', email: '', phone: '' });
    } catch (err) {
      // silent — form stays open
    } finally {
      setInlineLoading(false);
    }
  };

  const handleTimeSlotChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.map((slot, i) => 
        i === index ? value : slot
      )
    }));
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.leagueName.trim()) {
        setError('Please enter a league name');
        return;
      }
      if (!formData.city.trim() || formData.city.trim().length < 2) {
        setError('Please enter a city (at least 2 characters)');
        return;
      }
      if (!formData.state) {
        setError('Please select a state');
        return;
      }
      if (formData.hasGroup2) {
        if (formData.numTeamsGroup2 < 2) {
          setError('Group 2 must have at least 2 teams');
          return;
        }
        if (formData.numTeamsGroup2 % 2 !== 0) {
          setError('Group 2 must have an even number of teams');
          return;
        }
      }
    }
    
    if (step === 2) {
      if (formData.weeks.length === 0) {
        setError('Please add at least one week date');
        return;
      }
      // Check for empty dates in manual mode
      const hasEmptyDates = formData.weeks.some(week => !week.date);
      if (hasEmptyDates) {
        setError('Please fill in all week dates');
        return;
      }
      // Sync timeSlots array length with gamesPerWeek (handles user going back and changing the count)
      if (formData.timeSlots.length !== formData.gamesPerWeek) {
        const timeSlots = Array(formData.gamesPerWeek).fill('').map((_, i) =>
          i < formData.timeSlots.length ? formData.timeSlots[i] : ''
        );
        setFormData(prev => ({ ...prev, timeSlots }));
      }
    }

    if (step === 3) {
      // Check for empty time slots
      const hasEmptySlots = formData.timeSlots.some(slot => !slot.trim());
      if (hasEmptySlots) {
        setError('Please fill in all time slots');
        return;
      }
      // Rotating partners: skip team sync
      if (formData.formatType === 'rotating_partners') {
        setError('');
        setStep(step + 1);
        return;
      }
      // Sync teams array length with numTeams (handles user going back and changing the count)
      if (formData.teams.length !== formData.numTeams) {
        const teams = Array(formData.numTeams).fill(null).map((_, i) => {
          if (i < formData.teams.length) {
            const existing = formData.teams[i];
            return typeof existing === 'object' ? existing : { name: existing, captainId: null };
          }
          return { name: `Team ${i + 1}`, captainId: null };
        });
        setFormData(prev => ({ ...prev, teams }));
      }
      // Sync Group 2 teams array length with numTeamsGroup2
      if (formData.hasGroup2 && formData.teamsGroup2.length !== formData.numTeamsGroup2) {
        const teamsGroup2 = Array(formData.numTeamsGroup2).fill(null).map((_, i) => {
          if (i < formData.teamsGroup2.length) {
            const existing = formData.teamsGroup2[i];
            return typeof existing === 'object' ? existing : { name: existing, captainId: null };
          }
          return { name: `Group 2 Team ${i + 1}`, captainId: null };
        });
        setFormData(prev => ({ ...prev, teamsGroup2 }));
      }
    }

    if (step === 4 && formData.formatType === 'rotating_partners') {
      if (rpPlayers.length < 4) {
        setError('At least 4 players are required for a rotating-partner league');
        return;
      }
    }

    setError('');
    setStep(step + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const isRotating = formData.formatType === 'rotating_partners';

      // ── Rotating partners submission ──────────────────────────────────────
      if (isRotating) {
        const leagueData = {
          leagueName: formData.leagueName,
          sport: 'pickleball',
          formatType: 'rotating_partners',
          city: formData.city || null,
          state: formData.state || null,
          seasonYear: new Date().getFullYear().toString(),
          timeSlots: formData.timeSlots,
          allWeeks: formData.weeks,
          players: rpPlayers.map(p => ({
            name: p.name,
            email: p.email || null,
            skillRating: parseFloat(p.skillRating) || 3.5
          }))
        };

        const res = await api.post('/leagues', leagueData);
        const leagueId = res.data.league.id;

        // Generate the schedule immediately after creation
        const activeWeeks = formData.weeks.filter(w => !w.isHoliday);
        await api.post(`/leagues/${leagueId}/players/generate-schedule`, {
          weeks: activeWeeks,
          timeSlots: formData.timeSlots
        });

        if (onBack) onBack();
        return;
      }

      // ── Standard submission ───────────────────────────────────────────────

      // Filter out holiday weeks for schedule generation
      const activeWeeks = formData.weeks
        .filter(week => !week.isHoliday)
        .map(week => week.date);

      // Convert team objects to the shape expected by backend
      const roundRobinTeams = formData.teams.map(team => {
        const t = typeof team === 'object' ? team : { name: team, captainId: null };
        return {
          name: t.name,
          captainId: t.captainId || null,
          captain: '',
          email: ''
        };
      });

      const roundRobinGroup2Teams = formData.hasGroup2
        ? formData.teamsGroup2.map(team => {
            const t = typeof team === 'object' ? team : { name: team, captainId: null };
            return {
              name: t.name,
              captainId: t.captainId || null,
              captain: '',
              email: ''
            };
          })
        : [];

      // Build venue time slot map (filter out empty entries)
      const activeVenueMap = {};
      Object.entries(venueTimeSlotMap).forEach(([slot, venueId]) => {
        if (venueId) activeVenueMap[slot] = venueId;
      });

      const leagueData = {
        leagueName: formData.leagueName,
        sport: formData.sport,
        city: formData.city || null,
        state: formData.state || null,
        seasonYear: new Date().getFullYear().toString(),
        roundRobinTeams: roundRobinTeams,
        roundRobinGroup2Teams: roundRobinGroup2Teams,
        gameDates: activeWeeks,
        timeSlots: formData.timeSlots,
        allWeeks: formData.weeks,
        playoffSettings: formData.enablePlayoffs
          ? { enabled: true, teamsQualifying: formData.playoffTeams || formData.numTeams }
          : null,
        venueTimeSlotMap: Object.keys(activeVenueMap).length > 0 ? activeVenueMap : undefined,
        bestOf: formData.bestOf ? parseInt(formData.bestOf, 10) : null
      };

      if (actualLeagueId) {
        await api.put(`/leagues/${actualLeagueId}`, leagueData);
      } else {
        await api.post('/leagues', leagueData);
      }

      // Go back to dashboard
      if (onBack) onBack();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.error || err.response?.data?.errors?.join(', ') || 'Failed to create league');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="schedule-form-container">
      <div className="schedule-form-card">
        <h2>{actualLeagueId ? 'Edit League' : 'Create New Schedule'}</h2>
        
        <div className="progress-bar">
          {[
            { num: '1', label: 'Basic Info' },
            { num: '2', label: 'Dates' },
            { num: '3', label: 'Times' },
            { num: '4', label: 'Teams' },
            { num: '5', label: 'Review' },
          ].map(({ num, label }, i) => {
            const stepNum = i + 1;
            return (
              <button
                key={stepNum}
                type="button"
                className={`progress-step ${step >= stepNum ? 'active' : ''} ${step === stepNum ? 'current' : ''}`}
                onClick={() => setStep(stepNum)}
              >
                <span className="step-num">{num}</span>
                <span className="step-label">{label}</span>
              </button>
            );
          })}
        </div>

        {error && <div className="error-message">{error}</div>}

        {step === 1 && (
          <div className="form-step">
            <h3>League Information</h3>
            <div className="form-group">
              <label>League Name</label>
              <input
                type="text"
                name="leagueName"
                value={formData.leagueName}
                onChange={handleInputChange}
                placeholder="Enter league name"
              />
            </div>
            <div className="form-group">
              <label>Sport</label>
              <select
                name="sport"
                value={formData.sport}
                onChange={handleInputChange}
                className="form-select"
              >
                {Object.entries(getAllSportConfigs()).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
            {formData.sport === 'pickleball' && (
              <div className="form-group">
                <label>League Format</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, formatType: 'standard' }))}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: '2px solid',
                      borderColor: formData.formatType === 'standard' ? 'var(--color-brand)' : 'var(--color-input-border)',
                      background: formData.formatType === 'standard' ? 'var(--color-brand)' : 'var(--color-input-bg)',
                      color: formData.formatType === 'standard' ? '#fff' : 'var(--color-text)',
                      cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500
                    }}
                  >
                    Standard Teams
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, formatType: 'rotating_partners' }))}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: '2px solid',
                      borderColor: formData.formatType === 'rotating_partners' ? 'var(--color-brand)' : 'var(--color-input-border)',
                      background: formData.formatType === 'rotating_partners' ? 'var(--color-brand)' : 'var(--color-input-bg)',
                      color: formData.formatType === 'rotating_partners' ? '#fff' : 'var(--color-text)',
                      cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500
                    }}
                  >
                    Rotating Partners
                  </button>
                </div>
                {formData.formatType === 'rotating_partners' && (
                  <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '6px' }}>
                    Players are re-paired each week. Partners rotate to maximise variety and balance skill levels.
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., San Diego"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>State</label>
                <select
                  name="state"
                  value={formData.state || ''}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select state...</option>
                  {US_STATES.map(s => (
                    <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {(formData.sport === 'pool' || formData.sport === 'darts') && (
              <div className="form-group">
                <label>Best-of Format</label>
                <select
                  name="bestOf"
                  value={formData.bestOf || ''}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">No limit</option>
                  <option value="3">Best of 3</option>
                  <option value="5">Best of 5</option>
                  <option value="7">Best of 7</option>
                  <option value="9">Best of 9</option>
                  <option value="11">Best of 11</option>
                </select>
                <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                  {formData.bestOf ? `First to ${Math.ceil(parseInt(formData.bestOf) / 2)} wins the match` : 'Scores will not have an upper limit'}
                </p>
              </div>
            )}
            {formData.formatType !== 'rotating_partners' && (
              <>
                <div className="form-group">
                  <label>Number of Teams</label>
                  <input
                    type="number"
                    name="numTeams"
                    value={formData.numTeams}
                    onChange={handleInputChange}
                    min="4"
                    max={userPlan === 'free' ? 8 : 20}
                  />
                  {userPlan === 'free' && (
                    <p style={{ fontSize: '0.8rem', color: '#764ba2', marginTop: '4px' }}>
                      Pickup plan: up to 8 teams. Upgrade to League for unlimited teams.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>Games Per Week</label>
                  <input
                    type="number"
                    name="gamesPerWeek"
                    value={formData.gamesPerWeek}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                  />
                </div>
                <div className="form-group">
                  <div className="sf-toggle-row">
                    <label className="sf-toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.hasGroup2}
                        onChange={(e) => setFormData(prev => ({ ...prev, hasGroup2: e.target.checked }))}
                      />
                      <span className="sf-toggle-slider"></span>
                    </label>
                    <span className="sf-toggle-label">Add a second group</span>
                  </div>
                  <p className="step-description" style={{ marginTop: '6px' }}>A second group of teams that plays their own round-robin using the same time slots.</p>
                </div>
                {formData.hasGroup2 && (
                  <div className="form-group">
                    <label>Number of Teams in Group 2</label>
                    <input
                      type="number"
                      name="numTeamsGroup2"
                      value={formData.numTeamsGroup2}
                      onChange={handleInputChange}
                      min="2"
                      max={userPlan === 'free' ? Math.max(2, 8 - (parseInt(formData.numTeams) || 0)) : 20}
                    />
                  </div>
                )}
              </>
            )}
            {formData.formatType === 'rotating_partners' && (
              <div className="form-group">
                <label>Courts Per Week</label>
                <input
                  type="number"
                  name="gamesPerWeek"
                  value={formData.gamesPerWeek}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                />
                <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                  One game per court per week. You'll add players in Step 4.
                </p>
              </div>
            )}
            {formData.formatType !== 'rotating_partners' && (
              <>
                <div className="form-group">
                  <div className={`sf-toggle-row${userPlan !== 'pro' ? ' sf-toggle-disabled' : ''}`}>
                    <label className="sf-toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.enablePlayoffs}
                        onChange={(e) => setFormData(prev => ({ ...prev, enablePlayoffs: e.target.checked }))}
                        disabled={userPlan !== 'pro'}
                      />
                      <span className="sf-toggle-slider"></span>
                    </label>
                    <span className="sf-toggle-label">Enable playoffs</span>
                  </div>
                  <p className="step-description" style={{ marginTop: '6px' }}>Add a single-elimination playoff bracket after the regular season.</p>
                  {userPlan !== 'pro' && (
                    <p style={{ fontSize: '0.8rem', color: '#764ba2', marginTop: '4px' }}>
                      Playoffs require a Commissioner plan. Upgrade to Commissioner to enable.
                    </p>
                  )}
                </div>
                {formData.enablePlayoffs && (
                  <div className="form-group">
                    <label>Playoff Teams</label>
                    <select
                      value={formData.playoffTeams}
                      onChange={(e) => setFormData(prev => ({ ...prev, playoffTeams: parseInt(e.target.value) }))}
                      className="schedule-form-select"
                    >
                      <option value={0}>All teams</option>
                      {Array.from({ length: Math.floor((formData.numTeams - 2) / 2) }, (_, i) => formData.numTeams - (i + 1) * 2)
                        .filter(n => n >= 2)
                        .map(n => (
                          <option key={n} value={n}>Top {n} teams</option>
                        ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="form-step">
            <h3>Schedule Dates</h3>
            
            <div className="date-mode-selector">
              <button
                type="button"
                className={`mode-button ${dateMode === 'manual' ? 'active' : ''}`}
                onClick={() => setDateMode('manual')}
              >
                Manual Entry
              </button>
              <button
                type="button"
                className={`mode-button ${dateMode === 'auto' ? 'active' : ''}`}
                onClick={() => setDateMode('auto')}
              >
                Auto-Generate
              </button>
            </div>

            {dateMode === 'auto' && (
              <div className="auto-date-generator">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={autoStartDate}
                    onChange={(e) => setAutoStartDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Number of Weeks</label>
                  <input
                    type="number"
                    value={autoNumWeeks}
                    onChange={(e) => setAutoNumWeeks(parseInt(e.target.value))}
                    min="1"
                    max="52"
                  />
                </div>
                <button
                  type="button"
                  className="generate-button"
                  onClick={generateAutoDates}
                >
                  Generate Dates
                </button>
              </div>
            )}

            <div className="weeks-list">
              {formData.weeks.map((week, index) => (
                <div key={index} className={`week-item ${week.isHoliday ? 'holiday' : ''}`}>
                  <span className="week-label">Week {index + 1}</span>
                  <input
                    type="date"
                    value={week.date}
                    onChange={(e) => handleWeekDateChange(index, e.target.value)}
                    disabled={week.isHoliday}
                  />
                  <button
                    type="button"
                    className={`holiday-toggle ${week.isHoliday ? 'active' : ''}`}
                    onClick={() => toggleHoliday(index)}
                    title={week.isHoliday ? 'Mark as regular week' : 'Mark as holiday'}
                  >
                    {week.isHoliday ? '🎄 Holiday' : '📅 Regular'}
                  </button>
                  <button
                    type="button"
                    className="remove-button"
                    onClick={() => removeWeek(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {dateMode === 'manual' && (
              <button
                type="button"
                className="add-week-button"
                onClick={addManualWeek}
              >
                + Add Week
              </button>
            )}

            <div className="week-summary">
              <p>Total weeks: {formData.weeks.length}</p>
              <p>Game weeks: {formData.weeks.filter(w => !w.isHoliday).length}</p>
              <p>Holiday weeks: {formData.weeks.filter(w => w.isHoliday).length}</p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="form-step">
            <h3>Game Time Slots</h3>
            <p className="step-description">Select the start time for each game slot.</p>
            <div className="timeslots-list">
              {formData.timeSlots.map((slot, index) => (
                <div key={index} className="timeslot-input-group">
                  <label>Time Slot {index + 1}</label>
                  <input
                    type="time"
                    value={slot}
                    onChange={(e) => handleTimeSlotChange(index, e.target.value)}
                  />
                </div>
              ))}
            </div>

            {userPlan !== 'free' && formData.timeSlots.some(s => s) && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.25rem', color: 'var(--color-text)', fontSize: '0.95rem' }}>Default Venue per Time Slot</h4>
                <p className="step-description" style={{ marginBottom: '0.75rem' }}>Optionally assign a venue to each time slot. All games at that time will use this venue.</p>
                {formData.timeSlots.filter(s => s).map((slot, index) => {
                  // Format the time for display (convert 24h "18:00" to "6:00 PM")
                  const displayTime = slot;
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ minWidth: '70px', fontSize: '0.9rem', color: 'var(--color-text)' }}>{displayTime}</span>
                      {inlineVenueSlot === index ? (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                          <input
                            placeholder="Venue name *"
                            value={inlineVenueForm.name}
                            onChange={e => setInlineVenueForm(prev => ({ ...prev, name: e.target.value }))}
                            style={{ padding: '0.4rem 0.6rem', border: '1px solid var(--color-input-border)', borderRadius: '6px', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.85rem', width: '140px' }}
                          />
                          <input
                            placeholder="Address"
                            value={inlineVenueForm.address}
                            onChange={e => setInlineVenueForm(prev => ({ ...prev, address: e.target.value }))}
                            style={{ padding: '0.4rem 0.6rem', border: '1px solid var(--color-input-border)', borderRadius: '6px', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.85rem', width: '140px' }}
                          />
                          <button
                            disabled={!inlineVenueForm.name.trim() || inlineVenueLoading}
                            onClick={async () => {
                              setInlineVenueLoading(true);
                              try {
                                const result = await createVenue({
                                  name: inlineVenueForm.name.trim(),
                                  address: inlineVenueForm.address.trim() || null,
                                  fieldCount: inlineVenueForm.fieldCount || 1
                                });
                                const newVenue = result.venue;
                                setVenues(prev => [...prev, newVenue].sort((a, b) => a.name.localeCompare(b.name)));
                                setVenueTimeSlotMap(prev => ({ ...prev, [slot]: newVenue.id }));
                                setInlineVenueSlot(null);
                                setInlineVenueForm({ name: '', address: '', fieldCount: 1 });
                              } catch (err) {
                                setError(err.message);
                              } finally {
                                setInlineVenueLoading(false);
                              }
                            }}
                            style={{ padding: '0.4rem 0.7rem', background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                          >
                            {inlineVenueLoading ? 'Creating...' : 'Create'}
                          </button>
                          <button
                            onClick={() => { setInlineVenueSlot(null); setInlineVenueForm({ name: '', address: '', fieldCount: 1 }); }}
                            style={{ padding: '0.4rem 0.7rem', background: 'var(--color-input-bg)', color: 'var(--color-text)', border: '1px solid var(--color-input-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <select
                          value={venueTimeSlotMap[slot] || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '__create__') {
                              setInlineVenueSlot(index);
                            } else {
                              setVenueTimeSlotMap(prev => ({ ...prev, [slot]: val || null }));
                            }
                          }}
                          style={{ padding: '0.4rem 0.6rem', border: '1px solid var(--color-input-border)', borderRadius: '6px', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.85rem', flex: 1, maxWidth: '250px' }}
                        >
                          <option value="">No Venue</option>
                          {venues.map(v => (
                            <option key={v.id} value={v.id}>{v.name}</option>
                          ))}
                          <option value="__create__">+ Create New Venue</option>
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 4 && formData.formatType === 'rotating_partners' && (
          <div className="form-step">
            <h3>Players</h3>
            <p className="step-description">Add players and set their skill rating (1.0–6.0 DUPR scale). The scheduler uses ratings to create balanced matches.</p>

            {/* Add player form */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-section-bg)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '4px' }}>Name *</label>
                <input
                  type="text"
                  value={rpNewName}
                  onChange={e => setRpNewName(e.target.value)}
                  placeholder="Player name"
                  style={{ width: '100%', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--color-input-border)', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.9rem' }}
                />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '4px' }}>Email (optional)</label>
                <input
                  type="email"
                  value={rpNewEmail}
                  onChange={e => setRpNewEmail(e.target.value)}
                  placeholder="player@example.com"
                  style={{ width: '100%', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--color-input-border)', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.9rem' }}
                />
              </div>
              <div style={{ flex: '0 0 110px' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '4px' }}>Skill ({getSkillLabel(rpNewRating)})</label>
                <input
                  type="number"
                  value={rpNewRating}
                  onChange={e => setRpNewRating(e.target.value)}
                  min="1.0" max="6.0" step="0.5"
                  style={{ width: '100%', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--color-input-border)', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.9rem' }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const name = rpNewName.trim();
                  if (!name) return;
                  const rating = parseFloat(rpNewRating);
                  if (isNaN(rating) || rating < 1 || rating > 6) { setError('Skill rating must be 1.0–6.0'); return; }
                  setRpPlayers(prev => [...prev, { name, email: rpNewEmail.trim() || '', skillRating: rating }]);
                  setRpNewName(''); setRpNewEmail(''); setRpNewRating('3.5');
                  setError('');
                }}
                disabled={!rpNewName.trim()}
                style={{ padding: '0.5rem 1rem', background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', alignSelf: 'flex-end' }}
              >
                Add
              </button>
            </div>

            {rpPlayers.length === 0 ? (
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>No players added yet. Add at least 4.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {rpPlayers.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--color-section-bg)', borderRadius: '6px', border: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
                    {rpEditIndex === i ? (
                      <>
                        <input
                          value={rpEditData.name || ''}
                          onChange={e => setRpEditData(d => ({ ...d, name: e.target.value }))}
                          style={{ flex: '1 1 120px', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-input-border)', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.85rem' }}
                        />
                        <input
                          type="email"
                          value={rpEditData.email || ''}
                          onChange={e => setRpEditData(d => ({ ...d, email: e.target.value }))}
                          placeholder="email"
                          style={{ flex: '1 1 140px', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-input-border)', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.85rem' }}
                        />
                        <input
                          type="number"
                          value={rpEditData.skillRating || ''}
                          onChange={e => setRpEditData(d => ({ ...d, skillRating: e.target.value }))}
                          min="1" max="6" step="0.5"
                          style={{ width: '70px', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-input-border)', background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: '0.85rem' }}
                        />
                        <button type="button" onClick={() => {
                          setRpPlayers(prev => prev.map((pp, j) => j === i ? { ...pp, ...rpEditData, skillRating: parseFloat(rpEditData.skillRating) || pp.skillRating } : pp));
                          setRpEditIndex(null);
                        }} style={{ padding: '0.25rem 0.6rem', background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Save</button>
                        <button type="button" onClick={() => setRpEditIndex(null)} style={{ padding: '0.25rem 0.6rem', background: 'var(--color-input-bg)', color: 'var(--color-text)', border: '1px solid var(--color-input-border)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontWeight: 600, flex: '1 1 120px', fontSize: '0.9rem' }}>{p.name}</span>
                        {p.email && <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', flex: '1 1 140px' }}>{p.email}</span>}
                        <span style={{ fontSize: '0.85rem', color: 'var(--color-brand)', fontWeight: 500 }}>{p.skillRating} — {getSkillLabel(p.skillRating)}</span>
                        <button type="button" onClick={() => { setRpEditIndex(i); setRpEditData({ name: p.name, email: p.email || '', skillRating: p.skillRating }); }} style={{ padding: '0.25rem 0.6rem', background: 'var(--color-input-bg)', color: 'var(--color-text)', border: '1px solid var(--color-input-border)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Edit</button>
                        <button type="button" onClick={() => setRpPlayers(prev => prev.filter((_, j) => j !== i))} style={{ padding: '0.25rem 0.6rem', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Remove</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
              {rpPlayers.length} player{rpPlayers.length !== 1 ? 's' : ''} added
              {rpPlayers.length >= 4 && ` · ${Math.floor(rpPlayers.length / 4)} court${Math.floor(rpPlayers.length / 4) !== 1 ? 's' : ''} per week`}
            </p>
          </div>
        )}

        {step === 4 && formData.formatType !== 'rotating_partners' && (
          <div className="form-step">
            <h3>Team Names</h3>
            <p className="step-description">Name your teams and optionally assign captains from your library.</p>
            {formData.hasGroup2 && <h4 className="group-header">Group 1</h4>}
            <div className="teams-grid">
              {formData.teams.map((team, index) => {
                const teamObj = typeof team === 'object' ? team : { name: team, captainId: null };
                const selectedCaptain = captains.find(c => c.id === teamObj.captainId);
                return (
                  <div key={index} className="team-input-group">
                    <label>Team {index + 1}</label>
                    <input
                      type="text"
                      value={teamObj.name}
                      onChange={(e) => handleTeamChange(index, e.target.value)}
                      placeholder={`Team ${index + 1}`}
                    />
                    <div style={{ marginTop: '4px' }}>
                      <SearchableSelect
                        options={captains.map(c => ({ value: c.id, label: `${c.name}${c.email ? ` (${c.email})` : ''}` }))}
                        value={teamObj.captainId || ''}
                        onChange={(val) => handleTeamCaptainChange(index, val || null)}
                        placeholder="No Captain"
                        createLabel="+ Create New Captain"
                        onCreateNew={() => {
                          setInlineCreateIndex(index);
                          setInlineCreateGroup(1);
                          setInlineForm({ name: '', email: '', phone: '' });
                        }}
                      />
                    </div>
                    {selectedCaptain && selectedCaptain.email && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                        {selectedCaptain.email}
                      </span>
                    )}
                    {inlineCreateIndex === index && inlineCreateGroup === 1 && (
                      <div style={{ marginTop: '6px', padding: '8px', borderRadius: '6px', background: 'var(--color-section-bg)', border: '1px solid var(--color-border)' }}>
                        <input
                          type="text"
                          placeholder="Captain name *"
                          value={inlineForm.name}
                          onChange={(e) => setInlineForm(prev => ({ ...prev, name: e.target.value }))}
                          style={{ marginBottom: '4px', width: '100%' }}
                        />
                        <input
                          type="email"
                          placeholder="Email (optional)"
                          value={inlineForm.email}
                          onChange={(e) => setInlineForm(prev => ({ ...prev, email: e.target.value }))}
                          style={{ marginBottom: '4px', width: '100%' }}
                        />
                        <input
                          type="tel"
                          placeholder="Phone (optional)"
                          value={inlineForm.phone}
                          onChange={(e) => setInlineForm(prev => ({ ...prev, phone: e.target.value }))}
                          style={{ marginBottom: '6px', width: '100%' }}
                        />
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button type="button" className="btn-primary" style={{ fontSize: '0.8rem', padding: '4px 10px' }} disabled={inlineLoading || !inlineForm.name.trim()} onClick={() => handleInlineCreate(1, index)}>
                            {inlineLoading ? 'Saving...' : 'Save'}
                          </button>
                          <button type="button" className="btn-secondary" style={{ fontSize: '0.8rem', padding: '4px 10px' }} onClick={() => { setInlineCreateIndex(null); setInlineCreateGroup(null); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {formData.hasGroup2 && (
              <>
                <h4 className="group-header" style={{ marginTop: '2rem' }}>Group 2</h4>
                <div className="teams-grid">
                  {formData.teamsGroup2.map((team, index) => {
                    const teamObj = typeof team === 'object' ? team : { name: team, captainId: null };
                    const selectedCaptain = captains.find(c => c.id === teamObj.captainId);
                    return (
                      <div key={`g2-${index}`} className="team-input-group">
                        <label>Team {index + 1}</label>
                        <input
                          type="text"
                          value={teamObj.name}
                          onChange={(e) => handleTeamGroup2Change(index, e.target.value)}
                          placeholder={`Group 2 Team ${index + 1}`}
                        />
                        <div style={{ marginTop: '4px' }}>
                          <SearchableSelect
                            options={captains.map(c => ({ value: c.id, label: `${c.name}${c.email ? ` (${c.email})` : ''}` }))}
                            value={teamObj.captainId || ''}
                            onChange={(val) => handleTeamGroup2CaptainChange(index, val || null)}
                            placeholder="No Captain"
                            createLabel="+ Create New Captain"
                            onCreateNew={() => {
                              setInlineCreateIndex(index);
                              setInlineCreateGroup(2);
                              setInlineForm({ name: '', email: '', phone: '' });
                            }}
                          />
                        </div>
                        {selectedCaptain && selectedCaptain.email && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                            {selectedCaptain.email}
                          </span>
                        )}
                        {inlineCreateIndex === index && inlineCreateGroup === 2 && (
                          <div style={{ marginTop: '6px', padding: '8px', borderRadius: '6px', background: 'var(--color-section-bg)', border: '1px solid var(--color-border)' }}>
                            <input
                              type="text"
                              placeholder="Captain name *"
                              value={inlineForm.name}
                              onChange={(e) => setInlineForm(prev => ({ ...prev, name: e.target.value }))}
                              style={{ marginBottom: '4px', width: '100%' }}
                            />
                            <input
                              type="email"
                              placeholder="Email (optional)"
                              value={inlineForm.email}
                              onChange={(e) => setInlineForm(prev => ({ ...prev, email: e.target.value }))}
                              style={{ marginBottom: '4px', width: '100%' }}
                            />
                            <input
                              type="tel"
                              placeholder="Phone (optional)"
                              value={inlineForm.phone}
                              onChange={(e) => setInlineForm(prev => ({ ...prev, phone: e.target.value }))}
                              style={{ marginBottom: '6px', width: '100%' }}
                            />
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button type="button" className="btn-primary" style={{ fontSize: '0.8rem', padding: '4px 10px' }} disabled={inlineLoading || !inlineForm.name.trim()} onClick={() => handleInlineCreate(2, index)}>
                                {inlineLoading ? 'Saving...' : 'Save'}
                              </button>
                              <button type="button" className="btn-secondary" style={{ fontSize: '0.8rem', padding: '4px 10px' }} onClick={() => { setInlineCreateIndex(null); setInlineCreateGroup(null); }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="form-step">
            <h3>Review & Generate</h3>
            <div className="review-section">
              <div className="review-item">
                <strong>League Name:</strong> {formData.leagueName}
              </div>
              <div className="review-item">
                <strong>Location:</strong> {formData.city}, {formData.state}
              </div>
              {formData.formatType === 'rotating_partners' ? (
                <>
                  <div className="review-item">
                    <strong>Format:</strong> Rotating Partners (Pickleball)
                  </div>
                  <div className="review-item">
                    <strong>Courts Per Week:</strong> {formData.gamesPerWeek}
                  </div>
                  <div className="review-item">
                    <strong>Players ({rpPlayers.length}):</strong>
                    <ul>
                      {rpPlayers.map((p, i) => (
                        <li key={i}>{p.name} — {p.skillRating} ({getSkillLabel(p.skillRating)}){p.email ? ` · ${p.email}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className="review-item">
                    <strong>Number of Teams:</strong> {formData.numTeams}
                  </div>
                  <div className="review-item">
                    <strong>Games Per Week:</strong> {formData.gamesPerWeek}
                  </div>
                </>
              )}
              <div className="review-item">
                <strong>Total Weeks:</strong> {formData.weeks.length}
              </div>
              <div className="review-item">
                <strong>Game Weeks:</strong> {formData.weeks.filter(w => !w.isHoliday).length}
              </div>
              <div className="review-item">
                <strong>Holiday Weeks:</strong> {formData.weeks.filter(w => w.isHoliday).length}
              </div>
              {formData.formatType !== 'rotating_partners' && (
                <>
                  <div className="review-item">
                    <strong>{formData.hasGroup2 ? 'Group 1 Teams:' : 'Teams:'}</strong>
                    <ul>
                      {formData.teams.map((team, i) => {
                        const t = typeof team === 'object' ? team : { name: team, captainId: null };
                        const cap = captains.find(c => c.id === t.captainId);
                        return (
                          <li key={i}>
                            {t.name}
                            {cap && <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.85em' }}> — Capt: {cap.name}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  {formData.hasGroup2 && (
                    <div className="review-item">
                      <strong>Group 2 Teams:</strong>
                      <ul>
                        {formData.teamsGroup2.map((team, i) => {
                          const t = typeof team === 'object' ? team : { name: team, captainId: null };
                          const cap = captains.find(c => c.id === t.captainId);
                          return (
                            <li key={i}>
                              {t.name}
                              {cap && <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.85em' }}> — Capt: {cap.name}</span>}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {formData.bestOf && (
                    <div className="review-item">
                      <strong>Best-of Format:</strong> Best of {formData.bestOf} (first to {Math.ceil(parseInt(formData.bestOf) / 2)})
                    </div>
                  )}
                  {Object.keys(venueTimeSlotMap).filter(k => venueTimeSlotMap[k]).length > 0 && (
                    <div className="review-item">
                      <strong>Venue Assignments:</strong>
                      <ul>
                        {Object.entries(venueTimeSlotMap)
                          .filter(([, venueId]) => venueId)
                          .map(([slot, venueId]) => {
                            const venue = venues.find(v => v.id === venueId);
                            return <li key={slot}>{slot} &rarr; {venue?.name || 'Unknown'}</li>;
                          })}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className="form-navigation">
          {step > 1 && (
            <button onClick={prevStep} className="btn-secondary">
              Previous
            </button>
          )}
          {step < 5 ? (
            <button onClick={nextStep} className="btn-primary">
              Next
            </button>
          ) : (
            <button onClick={handleSubmit} className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : (actualLeagueId ? 'Update League' : 'Create League')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScheduleForm;
